// hooks/use-promo-codes.ts
import { useState } from "react";
import { toast } from "react-toastify";
import { IPromoCodeType } from '../../../utils/types';
import { useGetAllPromoCodes } from '../../../apis/promo-codes/get-all-promo-codes';
import { useDeletePromoCode } from '../../../apis/promo-codes/delete-promo';
import { useModifyPromoCodeActiveStatus } from '../../../apis/promo-codes/modify-promo-code-active-status';
import { formatDate } from '../../../utils/helpers';

const usePromoCodes = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [serviceType, setServiceType] = useState("");
    const [isActive, setIsActive] = useState("");
    const [filters, setFilters] = useState({ fromDate: "", toDate: "", serviceType: "", isActive: "" });
    const [selectedPromo, setSelectedPromo] = useState<IPromoCodeType | null>(null);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isViewPromoCodeVisible, setIsViewPromoCodeVisible] = useState(false);
    const [promoCodeStatusChangeId, setPromoCodeStatusChangeId] = useState("");

    const { data, isFetching: isLoading, refetch } = useGetAllPromoCodes({
        page: currentPage,
        startDate: filters.fromDate,
        expiryDate: filters.toDate,
        serviceType: filters.serviceType,
        isActive: filters.isActive,
    });

    const { mutateAsync: deletePromoCode, isPending: isDeletingPromoCode } = useDeletePromoCode();
    const { mutateAsync: modifyStatus } = useModifyPromoCodeActiveStatus();

    const promoCodes = data?.docs ?? [];
    const totalPages = data?.totalPages ?? 0;

    const handleSearch = () => {
        setCurrentPage(1);
        setFilters({ fromDate, toDate, serviceType, isActive });
    };

    const clearFilters = () => {
        setCurrentPage(1);
        setFromDate("");
        setToDate("");
        setServiceType("");
        setIsActive("");
        setFilters({ fromDate: "", toDate: "", serviceType: "", isActive: "" });
    };

    const handleDeletePromo = async () => {
        try {
            const res = await deletePromoCode(selectedPromo?._id!);
            toast.success(res?.message);
            setSelectedPromo(null);
            setIsConfirmationModalOpen(false);
            if (promoCodes.length <= 1 && currentPage > 1) {
                setCurrentPage((prev) => prev - 1);
            } else {
                refetch();
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.error || "Something went wrong");
        }
    };

    const handlePromoCodeActivationChange = async (id: string, isPromoCodeActive: boolean) => {
        setPromoCodeStatusChangeId(id);
        await modifyStatus({ id, isPromoCodeActive: !isPromoCodeActive });
        refetch();
        setPromoCodeStatusChangeId("");
    };

    const handleFromDateChange = (e: Date | null | undefined) =>
        setFromDate(formatDate(e?.toString() || ""));

    const handleToDateChange = (e: Date | null | undefined) =>
        setToDate(formatDate(e?.toString() || ""));

    const isApplyButtonDisabled = !serviceType && !isActive && !fromDate && !toDate;
    const isClearButtonDisabled = !fromDate && !toDate && !serviceType && !isActive;

    return {
        promoCodes,
        totalPages,
        isLoading,
        isDeletingPromoCode,
        currentPage,
        setCurrentPage,
        fromDate,
        toDate,
        serviceType,
        setServiceType,
        isActive,
        setIsActive,
        selectedPromo,
        setSelectedPromo,
        isAddModalVisible,
        setIsAddModalVisible,
        isConfirmationModalOpen,
        setIsConfirmationModalOpen,
        isEditModalOpen,
        setIsEditModalOpen,
        isViewPromoCodeVisible,
        setIsViewPromoCodeVisible,
        promoCodeStatusChangeId,
        handleSearch,
        clearFilters,
        handleDeletePromo,
        handlePromoCodeActivationChange,
        handleFromDateChange,
        handleToDateChange,
        isApplyButtonDisabled,
        isClearButtonDisabled,
        refetch,
    };
};

export default usePromoCodes;